import type React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, Input, Label, Spacer } from '@zillow/constellation';

import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { HEATING_OIL } from '../../../../constants';
import HeaderToggle from '../Utilities/HeaderToggle';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  children?: React.ReactNode;
  additionalFields?: object;
};

const INITIAL_STATE = {
  [HEATING_OIL.FIELD_NAME.IS_ENABLED]: true,
  [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
  [HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER]: '',
};

export default function HeatingOil_v1({ additionalFields, children, section, setSectionValues }: Props): JSX.Element {
  const heatingOil_isEnabled = section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED);
  const heatingOil_paysInAdditionIsToggled = section.getFieldValue(
    HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED),
      ...additionalFields,
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      ...additionalFields,
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Heating oil"
        subheader="Applies to this property"
        switchOn={heatingOil_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {heatingOil_isEnabled && (
        <>
          <CenteredHR />
          <SwitchWithTitle
            text="Tenant pays in addition to base rent"
            onClick={togglePaysInAddition}
            toggled={heatingOil_paysInAdditionIsToggled}
          />
          {heatingOil_paysInAdditionIsToggled && (
            <>
              <FormField marginTop="sm">
                <Label id="service-provider">
                  Your tenant will be responsible for cost and account set up and will pay directly to which service
                  provider?
                </Label>
                <Input
                  aria-labelledby="service-provider"
                  placeholder="Enter name of service provider"
                  value={section.getFieldValue(HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER)}
                  onChange={setServiceProvider}
                />
              </FormField>
              {children && <Spacer marginTop="sm">{children}</Spacer>}
            </>
          )}
        </>
      )}
    </>
  );
}
